// Role → allowed navigation entries + action permissions.
// Menus not listed for a role are hidden entirely from that role.

export const ROLE_LABELS = {
  admin: "Yönetici",
  stock_in: "Stok / Ürün",
  planner: "Planlama",
  cutter: "Kesim",
  sales: "Pazarlama",
  accounting: "Muhasebe",
  shipping: "Sevkiyat",
  viewer: "Görüntüleyici",
};

// Master sıralama — kullanıcı tarafından belirlenen sıra. Roller bu ana
// listeden sadece izinli olanları alır, sıra korunur.
export const NAV_ORDER = [
  "dashboard",         // PANEL
  "stock",             // STOK
  "cutting_tasks",     // KESİM GÖREVLERİ
  "factory_orders",    // FABRİKA SİPARİŞLERİ
  "retail_orders",     // PERAKENDE SİPARİŞLER
  "pending_orders",    // BEKLEYEN MÜŞTERİ SİPARİŞLERİ
  "reservations",      // OPSİYONLAR
  "shipping",          // SEVKİYAT
  "store_delivery",    // MAĞAZA TESLİM
  "cargo",             // KARGO TESLİM
  "kartela",           // KARTELA STOĞU
  "kartela_requests",  // KARTELA TALEPLERİ (kartela'yı gören herkes)
  "proforma",          // PROFORMA
  "blacklist",         // KARA LİSTE
  "retail_prices",     // PERAKENDE FİYATLAR
  "wholesale_prices",  // TOPTAN FİYATLAR
  "special_prices",    // ÖZEL FİYATLAR
  "purchase_prices",   // GELİŞ FİYATLARI
  "account_statements",// MUHASEBE EKSTRELERİ
  "returns",           // İADELER
  "cutting_history",   // KESİM GEÇMİŞİ
  "incoming_history",  // GELEN GEÇMİŞİ (sadece incoming_history_view yetkisi)
  "statistics",        // GENEL İSTATİSTİK (sadece statistics_view yetkisi)
  "products",          // ÜRÜNLER
  "factories",         // FABRİKALAR
  "customers",         // MÜŞTERİLER
  "customer_addresses",// MÜŞTERİ ADRESLERİ
  "sales_notes",       // PAZARLAMA NOT
  "my_customers",      // MÜŞTERİLERİM
  "defect_report",     // DEFO RAPORU
  "customer_stats",    // MÜŞTERİ İSTATİSTİĞİ
  "activity",          // İŞLEM GEÇMİŞİ
  "users",             // KULLANICILAR
  "backups",           // YEDEKLER
];

const ALLOWED_BY_ROLE = {
  admin:      new Set(NAV_ORDER),
  stock_in:   new Set(["dashboard", "stock", "cutting_tasks", "factory_orders", "retail_orders", "pending_orders", "store_delivery", "kartela", "blacklist", "retail_prices", "wholesale_prices", "special_prices", "returns", "cutting_history", "products", "factories", "defect_report", "reservations", "shipping", "proforma", "customer_addresses"]),
  planner:    new Set(["dashboard", "stock", "cutting_tasks", "factory_orders", "retail_orders", "pending_orders", "reservations", "shipping", "store_delivery", "cargo", "kartela", "proforma", "blacklist", "retail_prices", "special_prices", "returns", "cutting_history", "products", "factories", "customers", "customer_stats", "defect_report", "customer_addresses"]),
  cutter:     new Set(["dashboard", "stock", "cutting_tasks", "factory_orders", "retail_orders", "pending_orders", "shipping", "store_delivery", "cargo", "kartela", "blacklist", "retail_prices", "returns", "cutting_history", "defect_report", "customer_addresses"]),
  sales:      new Set(["dashboard", "stock", "cutting_tasks", "factory_orders", "retail_orders", "pending_orders", "shipping", "store_delivery", "cargo", "kartela", "proforma", "blacklist", "retail_prices", "wholesale_prices", "special_prices", "account_statements", "returns", "cutting_history", "factories", "sales_notes", "my_customers", "customer_stats", "customer_addresses"]),
  accounting: new Set(["dashboard", "stock", "factory_orders", "retail_orders", "pending_orders", "shipping", "store_delivery", "cargo", "kartela", "proforma", "blacklist", "retail_prices", "wholesale_prices", "special_prices", "purchase_prices", "account_statements", "returns", "cutting_history", "products", "factories", "customers", "my_customers", "defect_report", "customer_stats", "customer_addresses"]),
  shipping:   new Set(["dashboard", "stock", "retail_orders", "shipping", "store_delivery", "cargo", "kartela", "blacklist", "retail_prices", "returns", "customer_addresses"]),
  viewer:     new Set(["dashboard", "stock", "cutting_tasks", "factory_orders", "retail_orders", "pending_orders", "shipping", "store_delivery", "cargo", "kartela", "proforma", "blacklist", "retail_prices", "wholesale_prices", "special_prices", "purchase_prices", "account_statements", "returns", "cutting_history", "products", "factories", "customers", "sales_notes", "my_customers", "customer_stats", "customer_addresses"]),
};

export const NAV_BY_ROLE = Object.fromEntries(
  Object.entries(ALLOWED_BY_ROLE).map(([role, allowed]) => [
    role,
    NAV_ORDER.filter((key) => allowed.has(key)),
  ]),
);

/**
 * Kullanıcı için nav listesi:
 * - Rol tabanlı temel nav + user.extra_nav (kullanıcıya özel ek menü izinleri).
 * - Sıra NAV_ORDER'e göre korunur, çift kayıt olmaz.
 * Örnek: Hasan (stock_in) → extra_nav=["cargo"] verirsek Kargo Teslim'i görür.
 */
export function getNavForUser(user) {
  if (!user) return [];
  const role = typeof user === "string" ? user : user.role;
  const extraNav = (typeof user === "object" && user?.extra_nav) || [];
  const base = new Set(NAV_BY_ROLE[role] || []);
  extraNav.forEach((n) => base.add(n));
  if (base.has("kartela")) base.add("kartela_requests");
  if (typeof user === "object" && can(user, "incoming_history_view")) base.add("incoming_history");
  else base.delete("incoming_history");
  if (typeof user === "object" && can(user, "statistics_view")) base.add("statistics");
  else base.delete("statistics");
  return NAV_ORDER.filter((k) => base.has(k));
}

export const PERMS_BY_ROLE = {
  admin:      new Set(["all"]),
  stock_in:   new Set(["stock_write", "cut_write", "roll_delete", "roll_edit_initial", "cut_delete", "planning_write", "delivery_write", "product_write", "color_write", "return_write", "invoice_write", "kartela_write", "price_retail_read", "price_wholesale_read", "price_special_read", "read"]),
  planner:    new Set(["planning_write", "customer_write", "delivery_write", "product_write", "color_write", "return_write", "blacklist_write", "price_retail_read", "price_special_read", "read"]),
  cutter:     new Set(["cut_write", "delivery_write", "return_write", "kartela_write", "price_retail_read", "read"]),
  sales:      new Set(["delivery_write", "return_write", "price_retail_read", "price_wholesale_read", "price_special_read", "sales_note_write", "read"]),
  accounting: new Set(["return_write", "invoice_write", "product_write", "color_write", "price_write", "price_special_write", "price_purchase_write", "blacklist_write", "price_retail_read", "price_wholesale_read", "price_special_read", "price_purchase_read", "read"]),
  shipping:   new Set(["delivery_write", "return_write", "price_retail_read", "read"]),
  viewer:     new Set(["price_retail_read", "price_wholesale_read", "price_special_read", "price_purchase_read", "sales_note_read_all", "read"]),
};

export function can(userOrRole, perm) {
  if (!userOrRole) return false;
  const role = typeof userOrRole === "string" ? userOrRole : userOrRole.role;
  const extra = typeof userOrRole === "object" && userOrRole
    ? (userOrRole.extra_permissions || [])
    : [];
  const perms = PERMS_BY_ROLE[role] || new Set();
  return perms.has("all") || perms.has(perm) || extra.includes(perm);
}
